import { create } from 'zustand';

/** Mirrors the backend's BulkOperationJobStatus constants. */
export type BulkJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/** Mirrors the backend's BulkOperationTypes constants. */
export type BulkJobOperation = 'review' | 'status' | 'delete';

/** Which kind of job this is — both ride the same SignalR rail + store. */
export type JobKind = 'bulk' | 'import';

export interface BulkJobInfo {
  id: string;
  /** 'bulk' (default) or 'import'. Drives the label + which counters are shown. */
  jobType?: JobKind;
  /** Bulk: the operation (review/status/delete). Import: the format (standard/json/…). */
  operation: string;
  status: BulkJobStatus;
  total: number;
  processed: number;
  /** Bulk: contacts affected. Import: created + updated. */
  affected: number;
  skipped: number;
  /** Import-only breakdown (undefined for bulk jobs). */
  created?: number;
  updated?: number;
  /** Import-only error/row-failure count. */
  failed?: number;
  error?: string | null;
}

export function isBulkJobActive(status: BulkJobStatus): boolean {
  return status === 'queued' || status === 'running';
}

const OPERATION_LABELS: Record<string, string> = {
  review: 'Flag for review',
  status: 'Status update',
  delete: 'Delete',
};

const IMPORT_FORMAT_LABELS: Record<string, string> = {
  standard: 'Standard CSV import',
  simple: 'Simple CSV import',
  ecomail: 'Ecomail import',
  profile: 'Profile CSV import',
  json: 'JSON import',
};

export function bulkOperationLabel(operation: string): string {
  return OPERATION_LABELS[operation] ?? operation;
}

/** Human label for any job in the store, branching on its kind. */
export function jobLabel(job: BulkJobInfo): string {
  if (job.jobType === 'import') return IMPORT_FORMAT_LABELS[job.operation] ?? `Import (${job.operation})`;
  return bulkOperationLabel(job.operation);
}

interface BulkJobsState {
  /** Known jobs for the current tenant, keyed by job id. */
  jobs: Record<string, BulkJobInfo>;
  upsert: (job: BulkJobInfo) => void;
  remove: (id: string) => void;
  clear: () => void;
}

/**
 * Live bulk-job state, fed by the SignalR jobs hub (BulkJobsListener) and the
 * polling fallback. Read by the blocking progress dialog and the top-bar jobs
 * indicator. Cleared on tenant switch.
 */
export const useBulkJobsStore = create<BulkJobsState>((set) => ({
  jobs: {},

  upsert: (job) =>
    set((state) => {
      const existing = state.jobs[job.id];
      // Never regress: a stale poll result must not overwrite a terminal state
      // or roll the progress counter backwards.
      if (existing && !isBulkJobActive(existing.status)) return state;
      if (
        existing &&
        existing.status === job.status &&
        job.processed < existing.processed
      ) {
        return state;
      }
      return { jobs: { ...state.jobs, [job.id]: job } };
    }),

  remove: (id) =>
    set((state) => {
      if (!(id in state.jobs)) return state;
      const next = { ...state.jobs };
      delete next[id];
      return { jobs: next };
    }),

  clear: () => set({ jobs: {} }),
}));

export function runningBulkJobs(state: BulkJobsState): BulkJobInfo[] {
  return Object.values(state.jobs).filter((j) => isBulkJobActive(j.status));
}
